import api from '@/api';

export default {
  namespaced: true,
  state: {
    orders: [],
    currentOrder: null,
    loading: false,
    error: null
  },
  
  getters: {
    orders: (state) => state.orders || [],
    currentOrder: (state) => state.currentOrder,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    
    completedOrders: (state) => {
      return (state.orders || []).filter(o => o.status === 'delivered');
    },
    
    pendingOrders: (state) => {
      return (state.orders || []).filter(o => o.status === 'pending' || o.status === 'processing');
    }
  },
  
  mutations: {
    SET_ORDERS(state, orders) {
      state.orders = orders || [];
    },
    
    SET_CURRENT_ORDER(state, order) {
      state.currentOrder = order;
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    UPDATE_ORDER_STATUS(state, { orderNumber, status }) {
      const order = (state.orders || []).find(o => o.order_number === orderNumber);
      if (order) {
        order.status = status;
      }
      if (state.currentOrder?.order_number === orderNumber) {
        state.currentOrder.status = status;
      }
    },
    
    CLEAR_ERROR(state) {
      state.error = null;
    }
  },
  
  actions: {
    // Fetch all orders
    async fetchOrders({ commit }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await api.get('/orders/');
        let orders = [];
        
        if (Array.isArray(response.data)) {
          orders = response.data;
        } else if (response.data.results) {
          orders = response.data.results;
        } else {
          orders = response.data || [];
        }
        
        // Format orders for frontend
        const formattedOrders = orders.map(order => ({
          id: order.id,
          orderNumber: order.order_number,
          order_number: order.order_number,
          date: order.created_at,
          status: order.status,
          payment_status: order.payment_status,
          subtotal: parseFloat(order.subtotal),
          shipping_cost: parseFloat(order.shipping_cost),
          tax: parseFloat(order.tax),
          total: parseFloat(order.total),
          items: order.items || [],
          shipping_name: order.shipping_name,
          shipping_phone: order.shipping_phone,
          shipping_address: order.shipping_address,
          notes: order.notes
        }));
        
        commit('SET_ORDERS', formattedOrders);
        return formattedOrders;
      } catch (error) {
        console.error('Error fetching orders:', error);
        const errorMessage = error.response?.data?.error || 'Failed to fetch orders';
        commit('SET_ERROR', errorMessage);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Fetch single order
    async fetchOrder({ commit }, orderNumber) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await api.get(`/orders/${orderNumber}/`);
        const order = response.data;
        
        const formattedOrder = {
          id: order.id,
          orderNumber: order.order_number,
          order_number: order.order_number,
          date: order.created_at,
          status: order.status,
          payment_status: order.payment_status,
          subtotal: parseFloat(order.subtotal),
          shipping_cost: parseFloat(order.shipping_cost),
          tax: parseFloat(order.tax),
          total: parseFloat(order.total),
          items: (order.items || []).map(item => ({
            id: item.id,
            productId: item.product,
            name: item.product_name,
            price: parseFloat(item.product_price),
            quantity: item.quantity,
            total: parseFloat(item.total)
          })),
          shipping_name: order.shipping_name,
          shipping_phone: order.shipping_phone,
          shipping_address: order.shipping_address,
          notes: order.notes,
          created_at: order.created_at,
          updated_at: order.updated_at
        };
        
        commit('SET_CURRENT_ORDER', formattedOrder);
        return formattedOrder;
      } catch (error) {
        console.error('Error fetching order:', error);
        const errorMessage = error.response?.data?.error || 'Order not found';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Place order
    async placeOrder({ commit, dispatch }, orderData) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await api.post('/orders/create/', orderData);
        const order = response.data;
        
        // Refresh orders list
        await dispatch('fetchOrders');
        
        return order;
      } catch (error) {
        console.error('Error placing order:', error);
        const errorMessage = error.response?.data?.error || 'Failed to place order';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Update order status (admin only)
    async updateOrderStatus({ commit }, { orderNumber, status, paymentStatus }) {
      try {
        const response = await api.patch(`/orders/${orderNumber}/update-status/`, {
          status,
          payment_status: paymentStatus
        });
        
        commit('UPDATE_ORDER_STATUS', { orderNumber, status });
        return response.data;
      } catch (error) {
        console.error('Error updating order status:', error);
        throw error;
      }
    }
  }
};